import { computed, type Ref } from 'vue';
import type { SelectItem } from '@nuxt/ui';

/**
 * Select/input-menu items for the genre and stars filters, built from
 * whatever values are actually in use (see `usePieceFilters`'s `genreOptions`
 * / `starsOptions`), each with an "All" option prepended. Shared by every
 * piece table filter bar so the two freeform fields render identically.
 */
export function usePieceFilterItems(genreOptions: Ref<string[]>, starsOptions: Ref<number[]>) {
  const genreItems = computed<SelectItem[]>(() => [
    { label: 'All Genres', value: 'all' },
    ...genreOptions.value.map((value) => ({ label: value, value })),
  ]);

  // Star counts are freeform (a lesson piece has none, others 1-9+), so the
  // filter is an InputMenu seeded with 1-9 plus whatever values are already
  // in use, with room to type an exact count not yet listed.
  const starsSuggestions = computed(() => {
    const stars = new Set<number>(Array.from({ length: 9 }, (_, i) => i + 1));
    for (const value of starsOptions.value) stars.add(value);
    return [...stars].sort((a, b) => a - b);
  });
  const starsItems = computed(() => [
    { label: 'All Stars', value: 'all' as const },
    ...starsSuggestions.value.map((value) => ({ label: `${value} star${value === 1 ? '' : 's'}`, value })),
  ]);

  return { genreItems, starsItems };
}

/** Parses the InputMenu's typed-in "create" text into the stars filter it stands for. */
export function onCreateStarsFilter(starsFilter: Ref<number | 'all'>, item: string) {
  const value = Number(item);
  if (Number.isFinite(value)) starsFilter.value = value;
}
